import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GridOn as ExcelIcon,
  LibraryBooks as BulkIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import apiService from '../services/api.service';
import McsrReportView from '../components/mcsr/McsrReportView';
import McsrBulkDialog from '../components/mcsr/McsrBulkDialog';
import ResponsiveTable from '../components/common/ResponsiveTable';
import {
  McsrProjectListRow,
  McsrProjectOption,
  McsrReportPayload,
  fmtMcsr,
} from '../types/mcsr';

/**
 * MCSR Report view — the faithful reproduction of the SAP B1 Monthly Cost
 * Status Report, parameterized by Project + Period month (spec §1).
 * With no project selected it shows the multi-project overview (grand-total
 * measures per project); clicking a project opens its report.
 */

function monthBounds(month: string): { fdate: string; tdate: string } {
  // month = 'YYYY-MM'
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { fdate: `${month}-01`, tdate: `${month}-${String(last).padStart(2, '0')}` };
}

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export default function McsrReport() {
  const [projects, setProjects] = useState<McsrProjectOption[]>([]);
  const [months, setMonths] = useState<string[]>([]);
  const [project, setProject] = useState<McsrProjectOption | null>(null);
  const [month, setMonth] = useState<string>(currentMonth());
  const [payload, setPayload] = useState<McsrReportPayload | null>(null);
  const [overview, setOverview] = useState<McsrProjectListRow[]>([]);
  const [focusGroup, setFocusGroup] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
  const [emptyMessage, setEmptyMessage] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'warning' } | null>(null);
  const [showWarnings, setShowWarnings] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { fdate, tdate } = useMemo(() => monthBounds(month), [month]);

  useEffect(() => {
    apiService.getMcsrProjects()
      .then((res: any) => setProjects(res.data || []))
      .catch(() => setToast({ msg: 'Failed to load MCSR projects', sev: 'error' }));
  }, []);

  useEffect(() => {
    apiService.getMcsrMonths(project?.project)
      .then((res: any) => {
        const list: string[] = (res.data || []).map((d: string) => String(d).slice(0, 7));
        setMonths(Array.from(new Set(list)));
        if (list.length && !list.some((m) => m === month)) {
          setMonth(list[0]);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.project]);

  const load = useCallback(async () => {
    setLoading(true);
    setEmptyMessage(null);
    setFocusGroup(null);
    try {
      if (project) {
        const res: any = await apiService.getMcsrReport(project.project, fdate, tdate);
        setPayload(res.data || null);
        setOverview([]);
      } else {
        const res: any = await apiService.getMcsrOverview(fdate, tdate);
        setOverview(res.data || []);
        setPayload(null);
        if (!(res.data || []).length) setEmptyMessage('No MCSR snapshots in this period.');
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to load MCSR data';
      if (e?.response?.status === 404) {
        setPayload(null);
        setEmptyMessage('No MCSR snapshot for this project/month.');
      } else {
        setToast({ msg, sev: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [project, fdate, tdate]);

  useEffect(() => { void load(); }, [load]);

  const exportFile = async (kind: 'xlsx' | 'pdf') => {
    if (!project) return;
    setExporting(kind);
    try {
      const blob = await apiService.downloadMcsrExport(kind, project.project, fdate, tdate);
      downloadBlob(blob, `MCSR_${project.project}_${month}.${kind}`);
      setToast({ msg: `${kind.toUpperCase()} export ready`, sev: 'success' });
    } catch {
      setToast({ msg: `${kind.toUpperCase()} export failed`, sev: 'error' });
    } finally {
      setExporting(null);
    }
  };

  const warnings = [
    ...(payload?.validation.identityWarnings || []),
    ...(payload?.validation.sectionReconciliationWarnings || []),
  ];

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Monthly Cost Status Report
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Autocomplete
            sx={{ minWidth: 320, flex: 1 }}
            options={projects}
            value={project}
            onChange={(_, v) => setProject(v)}
            getOptionLabel={(o) => `${o.project} — ${o.projectName}`}
            isOptionEqualToValue={(a, b) => a.project === b.project}
            renderInput={(params) => (
              <TextField {...params} label="Project" placeholder="All projects (overview)" />
            )}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="mcsr-month-label">Period (month)</InputLabel>
            <Select
              labelId="mcsr-month-label"
              label="Period (month)"
              value={months.includes(month) ? month : ''}
              onChange={(e) => setMonth(String(e.target.value))}
            >
              {months.map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
              {!months.length && <MenuItem value={month}>{month}</MenuItem>}
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            startIcon={exporting === 'xlsx' ? <CircularProgress size={16} /> : <ExcelIcon />}
            disabled={!project || !!exporting}
            onClick={() => void exportFile('xlsx')}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={exporting === 'pdf' ? <CircularProgress size={16} /> : <PdfIcon />}
            disabled={!project || !!exporting}
            onClick={() => void exportFile('pdf')}
          >
            PDF
          </Button>
          <Button variant="contained" startIcon={<BulkIcon />} onClick={() => setBulkOpen(true)}>
            Bulk print / export
          </Button>
        </Stack>
        {payload?.header.customerCode && (
          <Typography sx={{ mt: 1, fontSize: 13, color: 'text.secondary' }}>
            Customer: {payload.header.customerCode} — {payload.header.customerName}
          </Typography>
        )}
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading MCSR data" />
        </Box>
      )}

      {!loading && emptyMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>{emptyMessage}</Alert>
      )}

      {!loading && warnings.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setShowWarnings((s) => !s)}>
              {showWarnings ? 'Hide' : `Show ${warnings.length}`}
            </Button>
          }
        >
          Background validation found {warnings.length} reconciliation warning{warnings.length === 1 ? '' : 's'} —
          stored values are displayed unchanged.
          <Collapse in={showWarnings}>
            <Box component="ul" sx={{ m: 0.5, pl: 2.5, fontSize: 12 }}>
              {warnings.map((w, i) => (<li key={i}>{w}</li>))}
            </Box>
          </Collapse>
        </Alert>
      )}

      {!loading && focusGroup && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setFocusGroup(null)}>
          Detail filtered to cost group “{focusGroup}” — close to show all groups.
        </Alert>
      )}

      {!loading && payload && (
        <McsrReportView
          payload={payload}
          focusGroup={focusGroup}
          onDrillGroup={(g) => setFocusGroup(g)}
        />
      )}

      {!loading && !payload && !project && overview.length > 0 && (
        <ResponsiveTable
          ariaLabel="MCSR projects overview"
          rows={overview}
          getRowKey={(r: McsrProjectListRow) => r.project}
          onRowClick={(r: McsrProjectListRow) => {
            const opt = projects.find((p) => p.project === r.project);
            setProject(opt || { project: r.project, projectName: r.projectName, customerCode: r.customerCode, customerName: r.customerName });
          }}
          columns={[
            { id: 'project', header: 'Project', cell: (r: McsrProjectListRow) => <strong>{r.project}</strong> },
            { id: 'name', header: 'Project Name', cell: (r: McsrProjectListRow) => r.projectName },
            { id: 'customer', header: 'Customer', cell: (r: McsrProjectListRow) => `${r.customerCode || ''} ${r.customerName || ''}` },
            { id: 'month', header: 'Month', cell: (r: McsrProjectListRow) => r.monthYear },
            { id: 'cb', header: 'Current Budget', align: 'right', cell: (r: McsrProjectListRow) => fmtMcsr(r.currentBudget) },
            { id: 'eud', header: 'Expended Upto Date', align: 'right', cell: (r: McsrProjectListRow) => fmtMcsr(r.expendedUptoDate) },
            { id: 'ctd', header: 'Committed to Date', align: 'right', cell: (r: McsrProjectListRow) => fmtMcsr(r.committedToDate) },
            { id: 'cff', header: 'Current Final Forecast', align: 'right', cell: (r: McsrProjectListRow) => fmtMcsr(r.currentFinalForecast) },
            { id: 'ou', header: 'Over/UnderRun', align: 'right', cell: (r: McsrProjectListRow) => fmtMcsr(r.overUnderRun) },
          ]}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.sev || 'info'} onClose={() => setToast(null)}>{toast?.msg}</Alert>
      </Snackbar>
      <McsrBulkDialog open={bulkOpen} onClose={() => setBulkOpen(false)} defaultMonth={month} />
    </Box>
  );
}
